import { AspectRatio } from '../enums/aspect-ratio.enum';
import {
	PublishableVideoContentEvents,
	PublishableVideoPlayerEvents,
	PublishableVideoStickyEvents,
	PublishableVideoUserEvents
} from '../enums/publishable-events.enum';
import { VideoJsPlayerOptions } from '../interfaces/player-options.interface';
import Player from './_adapter';
import videojs, { VideoJsPlayer } from 'video.js';
import '../vendors/videojs-contrib-ads';
import '../vendors/videojs.ima-updated';
import { Store } from '../store';
import { ImaAdPlayer } from 'src/adPlayers/ima';
import { VideoConsoleLogger } from 'src/utils/video-logger';

const logger = new VideoConsoleLogger();

export interface VideoJsSetupOptions {
	html5?: {
		nativeTextTracks: boolean;
	};
	plugins?: {};
	techOrder?: string[];
	aspectRatio?: AspectRatio;
	fluid?: boolean;
}

const API_DEFAULT_SETUP: Partial<VideoJsSetupOptions> = {
	html5: {
		nativeTextTracks: false // https://github.com/videojs/video.js/issues/3202
	},
	plugins: {},
	techOrder: ['html5']
};

export class VJsPlayer extends Player {
	public setup: VideoJsSetupOptions;
	private _fluid: boolean;
	public api: VideoJsPlayer;
	public playlist: any;

	constructor(options: VideoJsPlayerOptions) {
		super({ ...options, ...{ name: 'videojs' } });

		logger.debug('Constructing VideoJS', options)();

		if ((window as any).videojs || !options) {
			this.logError('Unable to init player (missing API or options)');
			return;
		}

		this._fluid = options.fluid;
	}

	public async initApi(): Promise<void> {
		const that = this;
		this.setup = {
			...API_DEFAULT_SETUP,
			aspectRatio: this.aspectRatio,
			fluid: this._fluid
		};

		(window as any).HELP_IMPROVE_VIDEOJS = false;

		this.api = videojs(this.id, this.setup, async function () {
			const player = this;

			const store: Store = Store.get();
			const adPlayer: ImaAdPlayer = store.getInstance(that.id)?.adPlayer;

			(player as any).ima(adPlayer.imaOptions);
			adPlayer.setApi((player as any).ima);
		});

		return Promise.resolve();
	}

	protected initListeners() {}

	initApiListeners() {
		this.initCommonListeners();

		this.api.on(
			'seeking',
			this.publish.bind(this, PublishableVideoPlayerEvents.SEEKING)
		);
		this.api.on(
			'ended',
			this.publish.bind(this, PublishableVideoContentEvents.VIDEO_END)
		);

		this.api.on(
			'volumechange',
			function () {
				// Track unmute once by player
				if (
					this.api.volume() > 0 &&
					!this.api.muted() &&
					this.muted &&
					!this._userUnmuted
				) {
					this._userUnmuted = true;
					this.muted = false;
					this.publish(PublishableVideoPlayerEvents.UNMUTE);
				}
			}.bind(this)
		);
	}

	initErrorsListeners() {
		this.api.on(
			'error',
			function () {
				var message = this.api.error.message; // TODO: add error code
				this.logError(message);
			}.bind(this)
		);
	}

	postReady() {
		// maybe add this for all players but need a rework as jwplayer hit this event in his adPlayer
		this.publish(PublishableVideoStickyEvents.STICKY_READY);

		// Listen click / touch events on playback buttons to catch user interaction with player
		// WARNING: do not use Video.js API playToggle event listener for that as it fires for every player on the page
		(window as any).AufVideo.Utils.addClickListener(
			this.id,
			['vjs-play-control'],
			function () {
				this.publish(PublishableVideoUserEvents.TOGGLE_PLAY_PAUSE);
			}.bind(this)
		);
	}

	protected disableAds(): void {}

	protected prePlay = () => {
		this.publish(PublishableVideoPlayerEvents.START);
		this.publish(PublishableVideoStickyEvents.STICKY_CONTENT_START);
	};

	protected postPlay = () => {
		// TEMP: fix for IOS muted autoplay => https://github.com/videojs/video.js/issues/4373
		// on IOS all volume controls are hidden (panel, toggle and control)
		if ((window as any).af_isIOS()) {
			/*
			var volumePanel = this.getControls().volumePanel;
			volumePanel.el_.classList.remove('vjs-hidden'); // Force panel to show
			volumePanel.muteToggle.el_.classList.remove('vjs-hidden'); // Force mute toggle button to show
			volumePanel.volumeControl.el_.classList.remove('vjs-hidden'); // Force volume range control to show
			*/
		}
	};

	// ==== Player API functions

	// Playback functions
	public isPaused = () => this.api.paused();
	public changeSrc = (sources) => this.api.src(sources);

	// Sound
	public mute = () => this.api.muted(true);
	public unmute = () => {
		this.muted = false;
		this.publish(PublishableVideoPlayerEvents.UNMUTE);

		return this.api.muted(false);
	};

	// Player informations
	getContainer = () => this.api.contentEl();
	getFile = () => this.api.currentSrc();
	getPosition = () => this.api.currentTime();
	getDuration = () => this.api.duration();
	hasControls = () => this.api.controls();
	getControls = () => this.api.controlBar;
}

window.AufVideo = window.AufVideo || {};
window.AufVideo.VideoJS = VJsPlayer;
